import { PickPartial } from 'lib-shared-types-utils';
import {
  searchParamsToURLSearchParams,
  searchStringToParams,
} from 'lib-shared-url';
import { useCallback, useMemo } from 'react';
// eslint-disable-next-line no-restricted-imports
import { useLocation, useNavigate } from 'react-router-dom';
import { useLatest } from 'react-use';

import { AppPathWithSearchParams } from '../appPaths';

import {
  AppSearchParams,
  PatchParams,
  ReplaceParams,
  SetParam,
  SetParams,
  UnsetParam,
  UnsetParams,
  UseAppSearchParamsResult,
} from './useAppSearchParams.types';

/**
 * A hook that lets you work with the search params associated with an app path.
 * Reading and updating search params should in turn be done through this hook
 * as it facilitates complete type-safety.
 */
export const useAppSearchParams = <TPath extends AppPathWithSearchParams>(
  _path: TPath,
): UseAppSearchParamsResult<TPath> => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = useMemo(
    () => searchStringToParams<AppSearchParams<TPath>>(location.search),
    [location.search],
  );
  const latestSearchParams = useLatest(searchParams);

  const replaceParams = useCallback<ReplaceParams<TPath>>(
    (params, options = { replace: false }) => {
      const paramsString = searchParamsToURLSearchParams(params).toString();
      navigate(
        `${location.pathname}${paramsString ? `?${paramsString}` : ''}`,
        options,
      );
    },
    [navigate, location.pathname],
  );

  const patchParams = useCallback<PatchParams<TPath>>(
    ({ set, unset }, options) => {
      replaceParams(
        Object.entries({
          ...latestSearchParams.current,
          ...set,
        }).reduce(
          (params, [name, value]) =>
            unset.includes(name as keyof PickPartial<AppSearchParams<TPath>>)
              ? params
              : { ...params, [name]: value },
          {} as AppSearchParams<TPath>,
        ),
        options,
      );
    },
    [latestSearchParams, replaceParams],
  );

  const setParams = useCallback<SetParams<TPath>>(
    (params, options) => {
      replaceParams({ ...latestSearchParams.current, ...params }, options);
    },
    [latestSearchParams, replaceParams],
  );

  const setParam = useCallback<SetParam<TPath>>(
    (name, value, options) => {
      setParams({ [name]: value } as AppSearchParams<TPath>, options);
    },
    [setParams],
  );

  const unsetParams = useCallback<UnsetParams<TPath>>(
    (names, options) => {
      replaceParams(
        Object.entries(latestSearchParams.current).reduce(
          (params, [name, value]) =>
            names.includes(name as keyof PickPartial<AppSearchParams<TPath>>)
              ? params
              : { ...params, [name]: value },
          {} as AppSearchParams<TPath>,
        ),
        options,
      );
    },
    [latestSearchParams, replaceParams],
  );

  const unsetParam = useCallback<UnsetParam<TPath>>(
    (name, options) => {
      unsetParams([name], options);
    },
    [unsetParams],
  );

  return {
    paramsString: location.search,
    params: searchParams,
    setParams,
    setParam,
    unsetParams,
    unsetParam,
    patchParams,
    replaceParams,
  };
};
